/* ══════════════════════════════════════════════════════════════════════
   DAKUTX — reveal on scroll
   Paste into Assets as  dkx.js
   Then load it once from layout/theme.liquid, just before </body>:
       {{ 'dkx.js' | asset_url | script_tag }}

   Type and columns rise and fade as they come into view. That is the
   Zara-style move, and it is the one that carries most of the polish.

   Three deliberate decisions in here:

   1. Nothing is hidden by CSS alone. This script adds the class that
      turns hiding on, so if the file fails to load, or a browser blocks
      it, every word is still on the page. Hiding content behind
      JavaScript that might not run is how a page ends up blank.

   2. Anything already on screen when the page loads is not animated. It
      has been painted already, so fading it in would be a flash rather
      than a reveal.

   3. It targets existing classes, so no section markup had to change.
      Add a new section using the same classes and it animates for free.
   ══════════════════════════════════════════════════════════════════════ */
(function () {
  if (window.__dkxReveal) return;          // theme.liquid may include it twice
  window.__dkxReveal = true;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if ((mq && mq.matches) || !('IntersectionObserver' in window)) return;

  var SEL = [
    '.dkx h1', '.dkx h2',
    '.dkx .dkx-kicker', '.dkx .dkx-lede', '.dkx .dkx-hero-sub',
    '.dkx .dkx-body', '.dkx .dkx-col', '.dkx .dkx-route',
    '.dkx .dkx-chips span',
    '.dkx .dkx-ed-body', '.dkx .dkx-orbit-foot', '.dkx .dkx-orbit-leg-head', '.dkx .dkx-orbit-leg'
  ].join(',');

  document.documentElement.classList.add('dkx-anim');

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('dkx-in');
      io.unobserve(e.target);              // reveal once, never re-hide
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });

  function arm(root) {
    var nodes = (root || document).querySelectorAll(SEL);
    Array.prototype.forEach.call(nodes, function (n, i) {
      if (n.getAttribute('data-dkx-armed')) return;
      n.setAttribute('data-dkx-armed', '1');

      var box = n.getBoundingClientRect();
      if (box.top < window.innerHeight * 0.92) {
        n.classList.add('dkx-r', 'dkx-in'); // already visible, no reveal
        return;
      }
      n.classList.add('dkx-r');
      // Siblings land in sequence rather than all at once, counted within their
      // own parent, so the first paragraph of a block always comes first. A
      // page-wide count wrapped every four items and could put the second
      // paragraph ahead of the first.
      // Count only the matching siblings running consecutively before this one,
      // restarting at a grid break. A grid with two groups of columns either
      // side of a subhead kept counting across the break, so the second group
      // landed on 5,6,7,8 and the % 6 wrap put its first column last and its
      // second column first. The subhead is an h2, which SEL matches.
      var k = 0;
      for (var pv = n.previousElementSibling; pv; pv = pv.previousElementSibling) {
        if (pv.classList && pv.classList.contains('dkx-grid-break')) break;
        if (pv.matches && pv.matches(SEL)) k++;
      }
      n.style.transitionDelay = ((k % 6) * 160) + 'ms';
      io.observe(n);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { arm(); });
  } else {
    arm();
  }

  // The customiser rebuilds a section on every edit, so re-arm what it replaced.
  document.addEventListener('shopify:section:load', function (e) { arm(e.target); });
})();


/* ══════════════════════════════════════════════════════════════════════
   Parallax
   Drifts a picture upward as its section scrolls past, so the image and
   the page move at slightly different rates and the section reads as
   having depth.

   Two decisions worth knowing:

   1. It only writes a CSS custom property, never the transform itself.
      The image also carries a zoom, and setting transform from JS would
      wipe it. Composing them in CSS keeps both.

   2. It runs off requestAnimationFrame, not the scroll event. Scroll
      fires far more often than the screen redraws, and doing layout work
      on every one of those is how a page starts to feel heavy, which is
      the opposite of the point.
   ══════════════════════════════════════════════════════════════════════ */
(function () {
  if (window.__dkxParallax) return;
  window.__dkxParallax = true;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mq && mq.matches) return;

  var items = [], ticking = false;

  function collect() {
    items = Array.prototype.slice.call(document.querySelectorAll('[data-dkx-parallax]'));
    // Give each image enough overscan that it can never run out of picture.
    // The headroom is half the overscan on each side, and the furthest the
    // image ever travels is half its strength, so 1.5x leaves a real margin
    // rather than meeting the edge exactly, which is what showed a white gap.
    for (var i = 0; i < items.length; i++) {
      var s = parseFloat(items[i].getAttribute('data-dkx-parallax')) || 0;
      items[i].style.setProperty('--dkx-over', (s * 1.5) + '%');
    }
  }

  function frame() {
    ticking = false;
    var vh = window.innerHeight;
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      var box = el.getBoundingClientRect();
      if (box.bottom < 0 || box.top > vh) continue;   // off screen, skip the maths
      var strength = parseFloat(el.getAttribute('data-dkx-parallax')) || 0;
      if (!strength) continue;
      // Measure against the frame, not the picture. The picture is deliberately
      // taller than its frame, so using its height overstated the travel and
      // pushed it past its own edge.
      var frame = el.parentNode.getBoundingClientRect();
      // 0 as the frame enters the bottom of the window, 1 as it leaves the top.
      // Clamped, because a frame taller than the window can push it outside that
      // range and the image then keeps drifting after it has run out of picture.
      var progress = (vh - frame.top) / (vh + frame.height);
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;
      var travel = (frame.height * strength) / 100;
      el.style.setProperty('--dkx-par', (0.5 - progress) * travel + 'px');
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(frame);
  }

  function start() { collect(); frame(); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('shopify:section:load', function () { collect(); frame(); });
})();

/* ══ EDITORIAL DRIFT ══
   The image and the text in each row travel at different rates as the row
   crosses the window, so they read as connected but not pinned together.

   Written as its own pass rather than folded into the background parallax
   above. That one exists to move a picture inside a fixed frame and gives it
   overscan so it never runs out of image. These elements move in open
   whitespace with nothing to run out of, so the overscan maths would only get
   in the way. */
(function () {
  if (window.__dkxDrift) return;
  window.__dkxDrift = true;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mq && mq.matches) return;

  var items = [], ticking = false;

  function collect() {
    items = Array.prototype.slice.call(document.querySelectorAll('[data-dkx-drift]'));
  }

  function fadeFrame() {
    // Scroll-linked rather than triggered. A one-shot transition fires once and
    // then the element is simply there; tying opacity to position means the
    // dissolve actually tracks the scroll, which is what reads as deliberate.
    var vh = window.innerHeight;
    var nodes = document.querySelectorAll('[data-dkx-fade]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var box = el.getBoundingClientRect();
      // Fully out at 92% down the window, fully in by 58%.
      var t = (0.92 - (box.top / vh)) / (0.92 - 0.58);
      if (t < 0) t = 0;
      if (t > 1) t = 1;
      var eased = t * t * (3 - 2 * t);
      el.style.opacity = eased.toFixed(3);
      el.style.transform = 'translate3d(0,' + ((1 - eased) * 44).toFixed(1) + 'px,0)';
    }
  }

  function frame() {
    ticking = false;
    fadeFrame();
    // Below the stacking breakpoint the two columns sit one above the other,
    // so moving them at different rates just makes the gap between them breathe.
    if (window.innerWidth <= 860) {
      for (var j = 0; j < items.length; j++) items[j].style.transform = '';
      return;
    }
    var vh = window.innerHeight;
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      var row = el.parentNode;
      var box = row.getBoundingClientRect();
      if (box.bottom < -200 || box.top > vh + 200) continue;
      // 0 as the row enters the bottom of the window, 1 as it leaves the top.
      var progress = (vh - box.top) / (vh + box.height);
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;
      var strength = parseFloat(el.getAttribute('data-dkx-drift')) || 0;
      // Centred on 0 at the midpoint, so a row sits where it was designed to
      // sit when it is in the middle of the screen, and drifts either side.
      // Neutral at 0.57 rather than 0.5. Centring on the exact midpoint puts the
      // image at its highest just as the row is most readable, which reads as the
      // picture sitting above its text. Shifting the zero later drops it back.
      el.style.transform = 'translate3d(0,' + ((0.71 - progress) * strength).toFixed(2) + 'px,0)';
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(frame);
  }

  function init() { collect(); frame(); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () { collect(); frame(); }, { passive: true });
  document.addEventListener('shopify:section:load', init);
})();

/* ------------------------------------------------------------------
   Drawn marks. Each tick or cross gets .is-drawn as it comes into view;
   marks that arrive together draw one after another.
   ------------------------------------------------------------------ */
(function () {
  if (window.__dkxDraw) return;
  window.__dkxDraw = true;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if ((mq && mq.matches) || !('IntersectionObserver' in window)) return;

  var queue = [], timer = null;
  function flush() {
    queue.forEach(function (m, i) {
      m.style.setProperty('--dkx-draw-delay', (i * 220) + 'ms');
      m.classList.add('is-drawn');
    });
    queue = [];
    timer = null;
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      queue.push(e.target);
      io.unobserve(e.target);
    });
    if (queue.length && !timer) timer = setTimeout(flush, 60);
  }, { rootMargin: '0px 0px -18% 0px', threshold: 0.5 });

  // The badge seals start as soon as they show at the bottom of the screen.
  var ioEarly = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      queue.push(e.target);
      ioEarly.unobserve(e.target);
    });
    if (queue.length && !timer) timer = setTimeout(flush, 60);
  }, { rootMargin: '0px 0px 0px 0px', threshold: 0.05 });

  function arm(root) {
    var marks = (root || document).querySelectorAll('.dkx .dkx-mark');
    Array.prototype.forEach.call(marks, function (m) {
      if (m.getAttribute('data-dkx-draw') || !m.querySelector('.dkx-dp')) return;
      if (m.closest('.bg-dark .dkx-orbit-legend')) return;   // drawn on the orbit's beat instead
      m.setAttribute('data-dkx-draw', '1');
      if (m.closest('.dkx-badges')) { ioEarly.observe(m); return; }
      io.observe(m);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { arm(); });
  } else {
    arm();
  }
  document.addEventListener('shopify:section:load', function (e) { arm(e.target); });
})();

/* ------------------------------------------------------------------
   Orbit. The light starts as soon as a dark ring reaches the screen.
   The legend ticks draw one after another, quickly, when their row
   arrives.
   ------------------------------------------------------------------ */
(function () {
  if (window.__dkxOrbit) return;
  window.__dkxOrbit = true;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if ((mq && mq.matches) || !('IntersectionObserver' in window)) return;

  var BEAT = 3500, LAP = BEAT * 4;

  function start(ring) {
    if (!ring || ring.getAttribute('data-dkx-start')) return;
    ring.setAttribute('data-dkx-start', String(Date.now()));
    ring.classList.add('is-orbiting');
  }

  var ringIO = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      ringIO.unobserve(e.target);
      start(e.target);
    });
  }, { rootMargin: '0px 0px 15% 0px', threshold: 0 });

  var legIO = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      legIO.unobserve(e.target);
      var ring = e.target.parentElement && e.target.parentElement.querySelector('.dkx-orbit');
      start(ring);
      // Ticks draw in quick succession once the columns have risen in, rather
      // than waiting for the light to reach each name, which could take most
      // of a lap.
      Array.prototype.forEach.call(e.target.querySelectorAll('.dkx-mark'), function (m, i) {
        var delay = 450 + i * 280;
        m.style.setProperty('--dkx-draw-delay', delay + 'ms');
        m.classList.add('is-drawn');
      });
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.3 });

  function arm(root) {
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll('.dkx .bg-dark .dkx-orbit'), function (r) {
      if (r.getAttribute('data-dkx-ring')) return;
      r.setAttribute('data-dkx-ring', '1');
      ringIO.observe(r);
    });
    Array.prototype.forEach.call(scope.querySelectorAll('.dkx .bg-dark .dkx-orbit-legend'), function (l) {
      if (l.getAttribute('data-dkx-orbit')) return;
      l.setAttribute('data-dkx-orbit', '1');
      legIO.observe(l);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { arm(); });
  } else {
    arm();
  }
  document.addEventListener('shopify:section:load', function (e) { arm(e.target); });
})();

/* ------------------------------------------------------------------
   Scroll effects. Each one only runs where its page setting has put a
   hook in the markup, so pages without them are untouched.
   ------------------------------------------------------------------ */
(function () {
  if (window.__dkxFx) return;
  window.__dkxFx = true;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduced = function () { return !!(mq && mq.matches); };
  var each = function (list, fn) { Array.prototype.forEach.call(list, fn); };

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  /* 1. Sections fade into each other */
  var COL = { paper: [255, 255, 255], stone: [244, 244, 242], dark: [20, 20, 20], graphite: [30, 30, 32] };
  function dissolve() {
    var root = document.documentElement;
    if (!root.classList.contains('dkx-dissolve')) return;
    var secs = [];
    each(document.querySelectorAll('.dkx > div > div'), function (e) {
      var m = e.className.match(/\bbg-(paper|stone|dark)\b/);
      if (m) secs.push({ el: e, c: /\bbg-graphite\b/.test(e.className) ? COL.graphite : COL[m[1]] });
    });
    if (secs.length < 2) return;
    root.classList.add('dkx-dissolve-on');
    function mix(a, b, t) {
      return 'rgb(' + [0, 1, 2].map(function (i) { return Math.round(a[i] + (b[i] - a[i]) * t); }).join(',') + ')';
    }
    function frame() {
      var y = window.innerHeight * 0.5, base = window.innerHeight * 0.18;
      var ease = function (t) { t = Math.max(0, Math.min(1, t)); return t * t * (3 - 2 * t); };
      var rects = secs.map(function (s) { return s.el.getBoundingClientRect(); });
      var idx = -1, i;
      for (i = 0; i < rects.length; i++) { if (rects[i].top <= y && rects[i].bottom > y) { idx = i; break; } }
      var out;
      if (idx < 0) {
        out = secs[0].c;
        for (i = rects.length - 1; i >= 0; i--) { if (rects[i].bottom <= y) { out = secs[i].c; break; } }
        root.style.setProperty('--dkx-scroll-bg', mix(out, out, 0));
        return;
      }
      var r = rects[idx], zone = Math.min(base, r.height / 2), cur = secs[idx].c;
      if (idx + 1 < secs.length && r.bottom - y < zone) {
        root.style.setProperty('--dkx-scroll-bg', mix(cur, secs[idx + 1].c, ease(1 - (r.bottom - y) / zone) * 0.5));
      } else if (idx > 0 && y - r.top < zone) {
        root.style.setProperty('--dkx-scroll-bg', mix(cur, secs[idx - 1].c, ease(1 - (y - r.top) / zone) * 0.5));
      } else {
        root.style.setProperty('--dkx-scroll-bg', mix(cur, cur, 0));
      }
    }
    var ticking = false;
    function tick() { if (!ticking) { ticking = true; requestAnimationFrame(function () { ticking = false; frame(); }); } }
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    frame();
  }

  /* 2. The problem band holds still and each problem becomes its answer */
  function pin() {
    var bands = document.querySelectorAll('.dkx .has-pin');
    if (!bands.length) return;
    function live() { return !reduced() && window.innerWidth >= 900 && window.innerHeight >= 640; }
    function frame() {
      var on = live();
      each(bands, function (b) {
        b.classList.toggle('is-live', on);
        var track = b.querySelector('.dkx-pin-track');
        if (!track) return;
        var stats = track.querySelectorAll('.dkx-stat');
        if (!on) { each(stats, function (s) { s.classList.remove('is-flipped'); }); return; }
        var stage = track.querySelector('.dkx-pin-stage');
        var sh = stage ? stage.offsetHeight : 0;
        track.style.setProperty('--pin-h', sh + 'px');
        if (sh > window.innerHeight - 134) {
          b.classList.remove('is-live');
          each(stats, function (s) { s.classList.remove('is-flipped'); });
          return;
        }
        var top = Math.max(104, (window.innerHeight - sh) / 2);
        var r = track.getBoundingClientRect();
        var total = track.offsetHeight - sh;
        var p = total > 0 ? Math.min(1, Math.max(0, (top - r.top) / total)) : 0;
        var n = stats.length;
        each(stats, function (s, i) { s.classList.toggle('is-flipped', p > ((i + 1) / n) * 0.78); });
      });
    }
    var ticking = false;
    function tick() { if (!ticking) { ticking = true; requestAnimationFrame(function () { ticking = false; frame(); }); } }
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    frame();
  }

  /* 3. Sideways gallery arrows */
  function gallery(root) {
    each((root || document).querySelectorAll('.dkx .is-gallery'), function (g) {
      if (g.getAttribute('data-dkx-gal')) return;
      g.setAttribute('data-dkx-gal', '1');
      var nav = g.nextElementSibling;
      if (!nav || !nav.classList.contains('dkx-gal-nav')) return;
      nav.classList.add('is-ready');
      var btns = nav.querySelectorAll('.dkx-gal-btn');
      function step() {
        var c = g.querySelector('.dkx-col');
        var gap = parseFloat(getComputedStyle(g).columnGap) || 0;
        return c ? c.getBoundingClientRect().width + gap : g.clientWidth * 0.8;
      }
      function update() {
        var max = g.scrollWidth - g.clientWidth - 2;
        btns[0].disabled = g.scrollLeft <= 2;
        btns[1].disabled = g.scrollLeft >= max;
      }
      each(btns, function (b) {
        b.addEventListener('click', function () {
          g.scrollBy({ left: Number(b.getAttribute('data-dir')) * step(), behavior: reduced() ? 'auto' : 'smooth' });
        });
      });
      g.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  onReady(function () { dissolve(); pin(); gallery(); });
  document.addEventListener('shopify:section:load', function (e) { gallery(e.target); });
})();

/* ------------------------------------------------------------------
   Badge strip fade. As the strip scrolls up, it fades out between the
   point where its middle is halfway down the screen and the point where it
   is 36% down, after rising about 60% of the way up. It is fully gone before the
   black section below it takes over the middle of the screen.
   ------------------------------------------------------------------ */
(function () {
  if (window.__dkxBadgeFade) return;
  window.__dkxBadgeFade = true;

  var mqb = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduced = !!(mqb && mqb.matches);

  function init() {
    var bands = document.querySelectorAll('.dkx .dkx-badges');
    if (!bands.length) return;
    function frame() {
      var vh = window.innerHeight;
      Array.prototype.forEach.call(bands, function (b) {
        // Each strip sets how far up the screen it is when fully faded (64% by
        // default); the fade itself runs over the 14% of screen before that.
        if (b.getAttribute('data-fade-at') === 'off') { b.style.opacity = ''; b.style.transform = ''; return; }
        var at = (parseFloat(b.getAttribute('data-fade-at')) || 64) / 100;
        var end = vh * (1 - at), start = end + vh * 0.14;
        var r = b.getBoundingClientRect();
        var mid = r.top + r.height / 2;
        var t = (mid - end) / (start - end);
        t = Math.max(0, Math.min(1, t));
        b.style.opacity = String(t);
        // Rolls up as it fades, as if the black coming in pushes it out.
        if (!reduced) b.style.transform = 'translate3d(0,' + (-(1 - t) * 56).toFixed(1) + 'px,0)';
      });
    }
    var ticking = false;
    function tick() { if (!ticking) { ticking = true; requestAnimationFrame(function () { ticking = false; frame(); }); } }
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    frame();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ------------------------------------------------------------------
   Cards that turn black. Each card in a section set to ink is marked as
   it comes into view; cards arriving together turn in page order, 180ms
   apart.
   ------------------------------------------------------------------ */
(function () {
  if (window.__dkxInk) return;
  window.__dkxInk = true;
  if (!('IntersectionObserver' in window)) return;

  var queue = [], timer = null;
  function flush() {
    queue.sort(function (a, b) { return (a.compareDocumentPosition(b) & 4) ? -1 : 1; });
    queue.forEach(function (c, i) {
      // 160ms apart, matching the stagger dkx.js already puts on each card's
      // clip reveal, so a card's ink fills as that card arrives rather than
      // the whole row going dark at once. The wipe itself runs downward; the
      // left-to-right reading comes from this stagger, which is intended.
      c.style.setProperty('--ink-delay', (i * 160) + 'ms');
      c.classList.add('is-inked');
    });
    queue = [];
    timer = null;
  }
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      queue.push(e.target);
      io.unobserve(e.target);
    });
    if (queue.length && !timer) timer = setTimeout(flush, 80);
  }, { rootMargin: '0px 0px -30% 0px', threshold: 0.5 });

  function arm(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('.dkx .is-inking .dkx-col'), function (c) {
      if (c.getAttribute('data-dkx-ink')) return;
      c.setAttribute('data-dkx-ink', '1');
      io.observe(c);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { arm(); });
  else arm();
  document.addEventListener('shopify:section:load', function (e) { arm(e.target); });
})();

/* ── Lo-Res bit reveal ───────────────────────────────────────────────────
   Splits the small Lo-Res labels into one span per character and reveals
   them in sequence when they come into view, so the pixel face appears to
   be drawn rather than faded up.

   Only plain-text elements are touched. Anything holding child elements is
   skipped, so nothing with markup inside it gets flattened. The original
   string goes on aria-label and the spans are hidden from assistive tech,
   or a screen reader would spell the words out letter by letter. */
(function () {
  if (window.__dkxBits) return;
  window.__dkxBits = true;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mq && mq.matches) return;
  if (!('IntersectionObserver' in window)) return;

  var BITSEL = [
    '.dkx .dkx-eyebrow',
    '.dkx .dkx-kicker',
    '.dkx .dkx-split-hero .sh-kicker',
    '.dkx .dkx-split-hero .sh-sub',
    '.dkx .dkx-split-hero .sh-b',
    '.dkx .dkx-statement',
    '.dkx .dkx-badge-label',
    '.dkx .dkx-secmark',
    '.dkx .dkx-orbit-mark',
    '.dkx .dkx-grid.cols-3 .dkx-col h3',
    '.dkx .dkx-grid.cols-4 .dkx-col h3',
    '.dkx .dkx-route h3',
    '.dkx .dkx-orbit-point h3',
    '.dkx .dkx-orbit-leg h3',
    '.dkx .dkx-makes-sub'
  ].join(',');

  function split(el) {
    if (el.getAttribute('data-dkx-bits')) return false;
    if (el.children.length) return false;          // leave anything with markup alone
    var text = el.textContent;
    if (!text || !text.trim() || text.length > 200) return false;

    el.setAttribute('data-dkx-bits', '1');
    el.setAttribute('aria-label', text.replace(/\s+/g, ' ').trim());

    var wrap = document.createElement('span');
    wrap.setAttribute('aria-hidden', 'true');

    var i = 0;
    for (var c = 0; c < text.length; c++) {
      var ch = text.charAt(c);
      if (ch === '\n') { wrap.appendChild(document.createTextNode('\n')); continue; }
      var sp = document.createElement('span');
      sp.className = 'dkx-bit';
      sp.textContent = ch;
      sp.style.setProperty('--bit-i', i);
      sp.style.setProperty('--bit-j', (Math.random() * 2).toFixed(2));
      wrap.appendChild(sp);
      i++;
    }
    el.textContent = '';
    el.appendChild(wrap);
    el.classList.add('dkx-bits');
    return true;
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('dkx-bits-in');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });

  function arm(root) {
    var nodes = (root || document).querySelectorAll(BITSEL);
    Array.prototype.forEach.call(nodes, function (n) { if (split(n)) io.observe(n); });

    // Pixel icons are filled blocks, so the stroke-draw observer never sees
    // them and they would sit at opacity 0 for ever. Reveal them here, on
    // the same trigger as the type they sit beside.
    var marks = (root || document).querySelectorAll('.dkx .dkx-mark.is-pixel, .dkx .dkx-mark.is-glyph');
    Array.prototype.forEach.call(marks, function (m) {
      if (m.getAttribute('data-dkx-px')) return;
      m.setAttribute('data-dkx-px', '1');
      pxio.observe(m);
    });
  }

  var pxio = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      // Same jitter the characters use, so blocks and letters arrive with
      // the same texture rather than the icon wiping cleanly left to right.
      Array.prototype.forEach.call(e.target.querySelectorAll('rect'), function (r) {
        r.style.setProperty('--px-j', (Math.random() * 3).toFixed(2));
      });
      e.target.classList.add('is-drawn');
      pxio.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { arm(); });
  } else {
    arm();
  }
  document.addEventListener('shopify:section:load', function (e) { arm(e.target); });
})();

/* ── Things re-digitise on a loop ────────────────────────────────────────
   Two rhythms, so the strip never fires as a block.

   The badge labels are a pool: every tick one visible label is picked at
   random and replays. Something is always moving and nothing moves in
   unison, which is what a row of them firing together looked like.

   The hero word and the orbit mark keep their own steady beat, because
   those are single elements where a regular pulse reads as deliberate
   rather than as noise.

   Both stop while the tab is in the background or the element is off
   screen, so nothing animates to an empty room. */
(function () {
  if (window.__dkxStripLoop) return;
  window.__dkxStripLoop = true;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mq && mq.matches) return;
  if (!('IntersectionObserver' in window)) return;

  var POOL_TICK = 1100;   // how often one random label is picked
  var BEAT      = 5000;   // the steady beat for the hero word and orbit mark

  var pool = [], beat = [];

  function track(list) {
    return new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var i = list.indexOf(e.target);
        if (e.isIntersecting && i === -1) list.push(e.target);
        if (!e.isIntersecting && i !== -1) list.splice(i, 1);
      });
    }, { threshold: 0.2 });
  }
  var poolIO = track(pool), beatIO = track(beat);

  function replay(el) {
    if (!el.classList.contains('dkx-bits')) return;
    el.classList.remove('dkx-bits-in');
    var bits = el.querySelectorAll('.dkx-bit');
    // Track the real delay of the last character to land, rather than
    // estimating it from the count. The jitter is rolled here, so the
    // largest (index + jitter) is known exactly.
    var last = 0;
    Array.prototype.forEach.call(bits, function (b) {
      var j = Math.random() * 2;
      b.style.setProperty('--bit-j', j.toFixed(2));
      var i = parseFloat(b.style.getPropertyValue('--bit-i')) || 0;
      var d = (i + j) * 24;
      if (d > last) last = d;
    });
    void el.offsetWidth;
    el.classList.add('dkx-bits-in');
    relight(el, last);
  }

  /* The icon beside a label goes dark while the label redraws, then blinks
     back on at the moment the last character lands, using the real delay
     measured in replay rather than an estimate from the character count. */
  function relight(el, lastMs) {
    var badge = el.closest && el.closest('.dkx-badge');
    if (!badge) return;
    var ico = badge.querySelector('.dkx-badge-ico');
    if (!ico) return;
    ico.classList.remove('is-lit');
    ico.classList.add('is-off');
    clearTimeout(ico.__dkxRelight);
    ico.__dkxRelight = setTimeout(function () {
      ico.classList.remove('is-off');
      ico.classList.add('is-lit');
      setTimeout(function () { ico.classList.remove('is-lit'); }, 360);
    }, lastMs);   // exactly as the final character lands
  }

  setInterval(function () {
    if (document.hidden || !pool.length) return;
    replay(pool[Math.floor(Math.random() * pool.length)]);
  }, POOL_TICK);

  setInterval(function () {
    if (document.hidden) return;
    beat.forEach(replay);
  }, BEAT);

  function arm(root) {
    var r = root || document;
    Array.prototype.forEach.call(r.querySelectorAll('.dkx .dkx-badge-label'),
      function (n) { poolIO.observe(n); });
    Array.prototype.forEach.call(
      r.querySelectorAll('.dkx .dkx-orbit-mark, .dkx .dkx-split-hero .sh-b'),
      function (n) { beatIO.observe(n); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { arm(); });
  } else {
    arm();
  }
  document.addEventListener('shopify:section:load', function (e) { arm(e.target); });
})();

/* ── Nav hover re-digitise ───────────────────────────────────────────────
   Splits each menu label into characters and replays the reveal when the
   pointer enters it. The characters are visible unless the replay class is
   on, so nothing here can leave the menu unreadable. */
(function () {
  if (window.__dkxNavBits) return;
  window.__dkxNavBits = true;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mq && mq.matches) return;

  var SEL = '.header-menu .header-menu__item-text, .header-menu-new .header-menu__item-text,'
          + '.clientlogin';   // the client login and request access links sit outside the menu

  function split(el) {
    if (el.getAttribute('data-dkx-nav')) return false;
    if (el.children.length) return false;
    var text = el.textContent;
    if (!text || !text.trim() || text.length > 40) return false;
    el.setAttribute('data-dkx-nav', '1');
    var frag = document.createDocumentFragment(), i = 0;
    for (var c = 0; c < text.length; c++) {
      var ch = text.charAt(c);
      var sp = document.createElement('span');
      sp.className = 'dkx-bit';
      sp.textContent = ch === ' ' ? ' ' : ch;
      sp.style.setProperty('--bit-i', i++);
      frag.appendChild(sp);
    }
    el.textContent = '';
    el.appendChild(frag);
    el.classList.add('dkx-navbits');
    return true;
  }

  function replay(el) {
    var bits = el.querySelectorAll('.dkx-bit');
    Array.prototype.forEach.call(bits, function (b) {
      b.style.setProperty('--bit-j', (Math.random() * 2).toFixed(2));
    });
    el.classList.remove('is-replay');
    void el.offsetWidth;
    el.classList.add('is-replay');
    clearTimeout(el.__dkxT);
    el.__dkxT = setTimeout(function () { el.classList.remove('is-replay'); },
                           bits.length * 26 + 400);
  }

  function arm(root) {
    var nodes = (root || document).querySelectorAll(SEL);
    Array.prototype.forEach.call(nodes, function (n) {
      if (!split(n)) return;
      var host = n.closest ? (n.closest('.header-menu__item') || n) : n;
      host.addEventListener('mouseenter', function () { replay(n); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { arm(); });
  } else {
    arm();
  }
  document.addEventListener('shopify:section:load', function (e) { arm(e.target); });
})();
